
import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import BuyerLayout from "@/components/layouts/BuyerLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, CreditCard, Lock } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { orderApi } from "@/services/api";

// Mock order data
const mockOrdersData = {
  "order-123": {
    id: "order-123",
    associationName: "Sunset Condominiums",
    items: [
      { id: "doc-001", name: "Resale Certificate", price: 75 },
      { id: "doc-003", name: "Bylaws & CC&Rs", price: 45 },
    ],
    subtotal: 120.00,
    processingFee: 4.99,
    total: 124.99,
  },
  "order-456": {
    id: "order-456",
    associationName: "Highland Heights HOA",
    items: [
      { id: "doc-005", name: "Resale Package", price: 125 },
    ],
    subtotal: 125.00,
    processingFee: 4.99,
    total: 129.99,
  },
};

const Payment = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Form state
  const [cardName, setCardName] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [cvv, setCvv] = useState("");

  // Get order details
  const order = orderId ? mockOrdersData[orderId] : null;
  
  // Create payment mutation using the processPayment endpoint
  const createPaymentMutation = useMutation({
    mutationFn: (paymentReference: string) => {
      // Use the orderId directly as a number - no need to replace "order-" prefix
      const numericOrderId = parseInt(orderId);
      // We now only need to send payment_reference
      return orderApi.processPayment(numericOrderId, {
        payment_reference: paymentReference
      });
    },
    onSuccess: () => {
      toast({
        title: "Payment successful",
        description: "Your payment has been processed successfully."
      });
      
      // Redirect to confirmation page
      navigate(`/buyer/confirmation/${orderId}`);
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Payment failed",
        description: error.message || "There was an error processing your payment."
      });
      setIsProcessing(false);
    }
  });

  if (!order) {
    return (
      <BuyerLayout>
        <div className="space-y-6">
          <h1 className="text-3xl font-bold tracking-tight">Order Not Found</h1>
          <p className="text-gray-500">
            The order you're trying to access could not be found.
          </p>
          <Button onClick={() => navigate("/buyer/search")}>Start New Request</Button>
        </div>
      </BuyerLayout>
    );
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Simple validation
    if (!cardName || !cardNumber || !expiryDate || !cvv) {
      toast({
        variant: "destructive",
        title: "Missing information",
        description: "Please fill in all required fields."
      });
      return;
    }

    setIsProcessing(true);

    // In a real application, we'd use a payment processor
    // For now, we'll simulate a successful payment and store the record
    setTimeout(() => {
      // Only need to send a payment reference now
      const paymentReference = `CARD-${cardNumber.slice(-4)}-${Date.now()}`;
      
      createPaymentMutation.mutate(paymentReference);
    }, 1500);
  };

  const formatCardNumber = (value: string) => {
    const v = value.replace(/\s+/g, "").replace(/[^0-9]/gi, "");
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || "";
    const parts = [];

    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }

    if (parts.length) {
      return parts.join(" ");
    } else {
      return value;
    }
  };

  return (
    <BuyerLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Payment</h1>
          <p className="text-gray-500">
            Complete your payment for documents from {order.associationName}
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Order Summary</CardTitle>
              <CardDescription>
                Review your order details
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h3 className="font-medium mb-2">Documents</h3>
                  <ul className="space-y-2">
                    {order.items.map((item) => (
                      <li key={item.id} className="flex justify-between text-sm">
                        <span>{item.name}</span>
                        <span className="font-medium">${item.price.toFixed(2)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="border-t pt-4">
                  <div className="flex justify-between text-sm">
                    <span>Subtotal</span>
                    <span>${order.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm mt-2">
                    <span>Processing Fee</span>
                    <span>${order.processingFee.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-bold mt-4 pt-4 border-t">
                    <span>Total</span>
                    <span>${order.total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Payment Information</CardTitle>
              <CardDescription>
                Enter your payment details securely
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="cardName">Name on card</Label>
                  <Input 
                    id="cardName" 
                    placeholder="John Smith" 
                    value={cardName}
                    onChange={(e) => setCardName(e.target.value)}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="cardNumber">Card number</Label>
                  <div className="relative">
                    <Input 
                      id="cardNumber" 
                      placeholder="1234 5678 9012 3456" 
                      value={cardNumber}
                      onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                      maxLength={19}
                      required
                    />
                    <CreditCard className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="expiryDate">Expiry date</Label>
                    <Input 
                      id="expiryDate" 
                      placeholder="MM/YY" 
                      value={expiryDate}
                      onChange={(e) => setExpiryDate(e.target.value)}
                      maxLength={5}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cvv">CVV</Label>
                    <Input 
                      id="cvv" 
                      placeholder="123" 
                      value={cvv}
                      onChange={(e) => setCvv(e.target.value)}
                      maxLength={4}
                      required
                    />
                  </div>
                </div>
                
                <div className="flex items-center pt-2 text-sm text-muted-foreground">
                  <Lock className="mr-2 h-4 w-4" />
                  <span>Your payment information is secure and encrypted</span>
                </div>
                
                <div className="pt-4">
                  <Button 
                    type="submit" 
                    className="w-full" 
                    disabled={isProcessing}
                  >
                    {isProcessing ? (
                      <>
                        <span className="mr-2">Processing</span>
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      </>
                    ) : (
                      <>
                        Pay ${order.total.toFixed(2)}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
            <CardFooter className="flex justify-between border-t">
              <Button 
                variant="outline" 
                onClick={() => navigate(-1)}
                disabled={isProcessing}
              >
                Back
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    </BuyerLayout>
  );
};

export default Payment;
